import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons/faAngleLeft';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons/faAngleRight';

import { ScheduleEntry } from 'us-web-services';
import { useSharedTranslation } from '../hooks/useSharedTranslation';
import { Button, ButtonType } from '../Button/Button';
import {
  MONTHS,
  WEEKDAYS,
  addMonths,
  daysInMonth,
  fromIsoToDateKey,
  isSameDay,
  startOfMonth,
  toDateKey,
} from './utils';

interface Props {
  cursor: Date;
  onSelectDate: (date: Date) => void;
  schedule: ScheduleEntry[];
  namespace?: string;
}

interface Cell {
  date?: Date;
  key: string;
}

const buildCells = (year: number, month: number): Cell[] => {
  const first = startOfMonth(year, month);
  const firstWeekday = first.getDay();
  const total = daysInMonth(year, month);
  const cells: Cell[] = [];
  for (let i = 0; i < firstWeekday; i += 1) {
    cells.push({ key: `pad-${i}` });
  }
  for (let d = 1; d <= total; d += 1) {
    const date = new Date(year, month, d);
    cells.push({ date, key: toDateKey(date) });
  }
  return cells;
};

type DayBadge = { booked: boolean; pending: boolean; available: boolean };

export const MiniCalendar = ({
  cursor,
  onSelectDate,
  schedule,
  namespace,
}: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespace);
  const [displayed, setDisplayed] = useState<Date>(cursor);

  const year = displayed.getFullYear();
  const month = displayed.getMonth();

  const today = new Date();
  const cursorKey = toDateKey(cursor);

  const cells = buildCells(year, month);

  const byDay = useMemo(() => {
    const map = new Map<string, DayBadge>();
    for (const entry of schedule) {
      const key = fromIsoToDateKey(entry.start);
      if (!key) continue;
      const cur = map.get(key) ?? { booked: false, pending: false, available: false };
      if (entry.booking) {
        const code = entry.booking.status?.code;
        const pending = code === 'parent_booking'
          || code === 'parent_reschedule'
          || code === 'sitter_reschedule';
        if (pending) cur.pending = true;
        else cur.booked = true;
      } else {
        cur.available = true;
      }
      map.set(key, cur);
    }
    return map;
  }, [schedule]);

  return (
    <div className='bg-white rounded p-2'>
      <div className='flex items-center justify-center gap-2 mb-1'>
        <Button
          buttonType={ButtonType.tertiary}
          aria-label={t('pages.calendar.previousMonth')}
          className='!p-0 !min-w-0 text-sapphire'
          onClick={(): void => setDisplayed((d) => addMonths(d, -1))}
        >
          <FontAwesomeIcon icon={faAngleLeft} />
        </Button>
        <span className='text-sm font-semibold'>
          {MONTHS[month]} {year}
        </span>
        <Button
          buttonType={ButtonType.tertiary}
          aria-label={t('pages.calendar.nextMonth')}
          className='!p-0 !min-w-0 text-sapphire'
          onClick={(): void => setDisplayed((d) => addMonths(d, 1))}
        >
          <FontAwesomeIcon icon={faAngleRight} />
        </Button>
      </div>
      <div className='grid grid-cols-7 gap-0.5 text-center'>
        {WEEKDAYS.map((w) => (
          <div key={w} className='text-[10px] text-neutral-500 py-1'>
            {w.charAt(0)}
          </div>
        ))}
        {cells.map((cell) => {
          if (!cell.date) return <div key={cell.key} />;
          const isSelected = cell.key === cursorKey;
          const badge = byDay.get(cell.key);
          const cellDate = cell.date;
          return (
            <Button
              key={cell.key}
              buttonType={ButtonType.tertiary}
              onClick={(): void => onSelectDate(cellDate)}
              className={`relative py-1 rounded text-xs hover:bg-neutral-100 ${
                isSelected
                  ? 'bg-sapphire-50 !text-sapphire font-semibold hover:bg-sapphire-50'
                  : isSameDay(cellDate, today)
                  ? 'font-bold text-sapphire'
                  : 'text-neutral-700'
              }`}
            >
              {cellDate.getDate()}
              {(badge?.booked || badge?.pending || badge?.available) && (
                <span className='absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5'>
                  {badge.booked && (
                    <span className='w-1 h-1 rounded-full bg-sapphire' />
                  )}
                  {badge.pending && (
                    <span className='w-1 h-1 rounded-full bg-honey-500' />
                  )}
                  {badge.available && (
                    <span className='w-1 h-1 rounded-full bg-emerald' />
                  )}
                </span>
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
